/**
 * permission-engine — RDZEŃ rozstrzygania uprawnień (F24 Kawałek 1).
 *
 * Czysta logika, zero zależności od bazy i od Node — dlatego żyje tutaj, a nie w `apps/api`.
 * Serwer (`apps/api/lib/permissions/engine.ts`) i panel (podpowiedź „dziedziczone z…") liczą
 * TYM SAMYM kodem; inaczej duch dziedziczenia pokazywany w drzewie rozjechałby się z prawdą
 * serwerową przy pierwszej zmianie reguł.
 *
 * ── Model ────────────────────────────────────────────────────────────────────────────────
 * Reguła ma dwa kształty (dokładnie jeden z nich naraz — pilnuje tego CHECK w bazie):
 *   • KODOWA    (`permissionCode`) — kod katalogu `<moduł>.<encja>.<akcja>`, zawsze na poziomie
 *     firmy. Most do ~113 kodów spoza drzewa obiektów (słowniki, procesy, ustawienia) i cel
 *     przelewu ze starego modelu ról.
 *   • DRZEWIASTA (`action`) — podgląd/edycja/usuwanie na konkretnym obiekcie hierarchii.
 *
 * ── Zasady ────────────────────────────────────────────────────────────────────────────────
 * 1. KASKADA W DÓŁ (w obrębie JEDNEGO podmiotu): poziom szczegółowy nadpisuje ogólny. Prawo na
 *    typie FZ bije prawo na kartotece Dokumenty; przy remisie głębokości — zakaz.
 *    „Dziedziczone" = brak wiersza, czyli przeskok o poziom wyżej.
 * 2. TRZY WARSTWY PODMIOTÓW (decyzja 2026-09-24, ADR-0103): Użytkownik → Zespoły → Role.
 *    Rozstrzyga NAJWYŻSZA warstwa, która ma jakąkolwiek regułę obejmującą pytany obiekt
 *    (z dziedziczeniem w drzewie) — także wtedy, gdy jej reguła jest SZERSZA niż reguła warstwy
 *    niższej. Zespół „Zezwalaj" na całe Dokumenty + rola „Zabraniaj" na Faktury zakupu → wolno.
 * 3. WEWNĄTRZ WARSTWY (kilka ról, kilka zespołów) ZAKAZ WYGRYWA: każdy podmiot rozstrzyga się
 *    osobno (kaskadą z punktu 1), a jeden zakaz przesądza o wyniku warstwy, bez porównywania
 *    głębokości — inaczej zakaz jednej roli dałoby się obejść węższym prawem drugiej roli.
 * 4. Brak jakiejkolwiek reguły we wszystkich warstwach = BRAK DOSTĘPU.
 * 5. ETAP PROCESU to najgłębszy poziom: prawa do dokumentu zależą od tego, gdzie ten stoi
 *    w obiegu akceptacji. Dokument stojący JEDNOCZEŚNIE na kilku etapach równoległych pyta o
 *    każdy z nich osobno — ZAKAZ NA DOWOLNYM Z NICH przesądza całość (rozstrzyga wywołujący,
 *    iterując po kluczach etapów).
 *
 * Świadoma konsekwencja punktu 2: zakaz etapu nadany ROLI („nikt nie edytuje w trakcie
 * akceptacji") nie chroni osoby, która ma własną regułę albo regułę zespołu obejmującą ten
 * dokument. Do 24.09 obowiązywała zasada odwrotna (osobiste zezwolenie ustępowało węższemu
 * zakazowi grupy) — zmieniona decyzją właścicielki.
 */

/** Podmiot, któremu nadano regułę. */
export type PermissionSubjectKind = 'role' | 'team' | 'user';

/** Akcja drzewiasta. Kawałek 1: podgląd / edycja / usuwanie. */
export type PermissionAction = 'view' | 'edit' | 'delete';

/** Rodzaj obiektu — poziom hierarchii, na którym wisi reguła. */
export type PermissionObjectKind =
  | 'company'
  | 'kartoteka'
  | 'doc_class'
  | 'doc_type'
  | 'category'
  | 'process_any'
  | 'process_step';

/**
 * Klucz etapu dla dokumentu „bez procesu" — nigdy nie wysłanego do akceptacji albo takiego,
 * którego sprawę anulowano (technicznie także sprawa w toku bez bieżących etapów i inny status —
 * `RecordCaseState` w `apps/api/lib/processes/active-stage-sql.ts`).
 *
 * Sprawa ZAKOŃCZONA nie jest „bez procesu": jak w starym WF etap końcowy to zwykły etap, więc
 * dokument stoi na nim i jego prawa liczą się z tego etapu (patrz `ANY_PROCESS_STAGE_KEY`).
 *
 * Jest jawnym stanem w drzewie uprawnień (da się mu zabronić osobno), ale dopóki nikt nie nada
 * mu reguły, rozstrzyga poziom wyżej — typ dokumentu. Gdyby brak reguły znaczył brak dostępu,
 * po wdrożeniu zniknęłyby wszystkie dokumenty spoza procesu, a każdy nowo założony typ
 * startowałby niewidoczny.
 */
export const OUT_OF_PROCESS_STAGE_KEY = '__out_of_process__';

/**
 * Klucz „gdziekolwiek w obiegu" — obejmuje KAŻDY etap KAŻDEGO procesu przypiętego do typu,
 * także ETAP KOŃCOWY, na którym stoi dokument po zakończeniu sprawy.
 *
 * Powstał dla polityki, która jest powodem istnienia całej osi etapów: „nikt nie edytuje
 * dokumentu w trakcie akceptacji". Bez niego trzeba by nadać zakaz osobno na każdym procesie,
 * a proces przypięty do typu PÓŹNIEJ nie byłby objęty — cicho, bez żadnego sygnału.
 *
 * Leży MIĘDZY typem a konkretnym etapem: doprecyzowuje prawo typu, ale ustępuje regule nadanej
 * na wskazanym etapie (bieżącym albo końcowym). Rozstrzyga też dokument, którego sprawa jest
 * w błędzie albo zakończyła się na etapie końcowym bez trwałego klucza — taki dokument nie stoi
 * na żadnym adresowalnym etapie, ale jest „w procesie". Stan „poza procesem" nie jest nim
 * objęty — to dwa rozłączne światy.
 */
export const ANY_PROCESS_STAGE_KEY = '__any_process__';

export type PermissionEffect = 'allow' | 'deny';

/**
 * Głębokość poziomu obiektu. Im większa, tym węższa (bardziej szczegółowa) reguła.
 * `doc_type` i `category` to liście — nie współistnieją na jednej ścieżce.
 */
export const OBJECT_DEPTH: Record<PermissionObjectKind, number> = {
  company: 0,
  kartoteka: 1,
  doc_class: 2,
  doc_type: 3,
  category: 3,
  /**
   * „Gdziekolwiek w obiegu" — węższe niż typ, szersze niż wskazany etap. Współrzędna jak przy
   * etapie: `objectKey` niesie klucz zarezerwowany, `objectId` — id typu dokumentu.
   */
  process_any: 4,
  /**
   * Etap procesu wisi POD typem dokumentu — „na etapie Akceptacja kierownika nie wolno edytować"
   * doprecyzowuje prawo nadane typowi. Współrzędna etapu to para: `objectKey` niesie klucz etapu,
   * `objectId` — id typu dokumentu (rodzica), dzięki czemu ten sam etap pod dwoma typami pozostaje
   * rozróżnialny.
   */
  process_step: 5,
};

/** Współrzędna obiektu: rodzaj + referencja. „Wszystko" nie ma znacznika — jest nim poziom wyżej. */
export interface PermissionObjectRef {
  kind: PermissionObjectKind;
  /** Klucz kartoteki albo klasa dokumentu; przy liściu — dyskryminator (klasa / klucz kartoteki). */
  key?: string | null;
  /** Identyfikator typu dokumentu albo kategorii. */
  id?: string | null;
}

/**
 * Poziom etapu obiegu na ścieżce obiektu — dokładany POD typem (dokumentu albo zadania): rekord
 * STOJĄCY w obiegu przechodzi przez „gdziekolwiek w obiegu" (`process_any`), zanim dojdzie do
 * swojego etapu (`process_step`) — dzięki temu jedna reguła obejmuje wszystkie procesy typu, także
 * przypięte później. Stan „poza procesem" tego poziomu NIE przechodzi: to rozłączny świat, a nie
 * węższy przypadek bycia w obiegu. Obie współrzędne niosą id typu (rodzica).
 *
 * Jedno źródło tego kształtu dla serwera (ścieżka rekordu, `buildObjectPath`) i panelu (ścieżka
 * komórki Centrum, `cellObjectPath`) — inaczej „dziedziczone" w siatce rozjechałoby się z prawdą
 * serwerową.
 */
export function stagePathRefs(typeId: string | null, stageKey: string): PermissionObjectRef[] {
  const step: PermissionObjectRef = { kind: 'process_step', key: stageKey, id: typeId };
  if (stageKey === OUT_OF_PROCESS_STAGE_KEY) return [step];
  return [{ kind: 'process_any', key: ANY_PROCESS_STAGE_KEY, id: typeId }, step];
}

/** Reguła w postaci, w jakiej rozstrzyga ją silnik (lustro wiersza `permission_rules`). */
export interface PermissionRuleInput {
  subjectKind: PermissionSubjectKind;
  subjectId: string;
  permissionCode?: string | null;
  action?: PermissionAction | null;
  objectKind: PermissionObjectKind;
  objectKey?: string | null;
  objectId?: string | null;
  effect: PermissionEffect;
}

/** Czego dotyczy pytanie: kod katalogu + (opcjonalnie) ścieżka obiektu od korzenia do liścia. */
export interface PermissionQuery {
  code: string;
  /** Akcja drzewiasta wyprowadzona z kodu; `null` = kod spoza drzewa (rozstrzyga tylko kodowo). */
  action: PermissionAction | null;
  /**
   * Ścieżka obiektu OD NAJOGÓLNIEJSZEGO DO NAJWĘŻSZEGO, np.
   * [kartoteka documents, klasa purchase_invoice, typ FZ]. Poziom `company` jest dorozumiany.
   */
  path: PermissionObjectRef[];
}

/** Wynik z tropem — panel pokazuje „skąd to mam", a przy „dziedziczone" rysuje ducha. */
export interface PermissionDecision {
  allowed: boolean;
  /** Reguła, która rozstrzygnęła. `null` = brak jakiejkolwiek reguły (odmowa domyślna). */
  decidedBy: PermissionRuleInput | null;
}

const UNDETERMINED: PermissionDecision = { allowed: false, decidedBy: null };

/** Czy reguła leży NA ścieżce pytania (czyli obejmuje pytany obiekt). */
function ruleMatchesObject(rule: PermissionRuleInput, query: PermissionQuery): boolean {
  if (rule.objectKind === 'company') return true;
  return query.path.some(
    (node) =>
      node.kind === rule.objectKind &&
      (node.key ?? null) === (rule.objectKey ?? null) &&
      (node.id ?? null) === (rule.objectId ?? null),
  );
}

/**
 * Czy reguła w ogóle dotyczy pytania.
 *
 * Reguła KODOWA — tylko przy zgodności kodu. Reguła DRZEWIASTA — tylko gdy pytanie ma akcję
 * (kod należy do drzewa) i akcja się zgadza. Dzięki temu reguła „cała firma / podgląd" NIE
 * rozlewa się na kody administracyjne spoza drzewa — to celowa bariera, nie przeoczenie.
 */
function ruleApplies(rule: PermissionRuleInput, query: PermissionQuery): boolean {
  if (!ruleMatchesObject(rule, query)) return false;
  if (rule.permissionCode != null) return rule.permissionCode === query.code;
  if (rule.action == null) return false;
  return query.action != null && rule.action === query.action;
}

/**
 * Specyficzność reguły. Reguła kodowa bije akcyjną NA TYM SAMYM POZIOMIE, bo jest węższa:
 * dotyczy jednego kodu, a nie całej trójki podgląd/edycja/usuwanie.
 */
function specificity(rule: PermissionRuleInput): number {
  return OBJECT_DEPTH[rule.objectKind] * 2 + (rule.permissionCode != null ? 1 : 0);
}

/**
 * Rozstrzygnięcie w obrębie JEDNEJ warstwy podmiotów: wygrywa najwęższa reguła, a przy remisie
 * — zakaz. Brak pasujących reguł = nierozstrzygnięte (`decidedBy === null`).
 */
export function evaluateTier(
  rules: readonly PermissionRuleInput[],
  query: PermissionQuery,
): PermissionDecision {
  let best: PermissionRuleInput | null = null;
  let bestSpec = -1;

  for (const rule of rules) {
    if (!ruleApplies(rule, query)) continue;
    const spec = specificity(rule);
    if (spec > bestSpec) {
      best = rule;
      bestSpec = spec;
      continue;
    }
    // Remis głębokości → zakaz wygrywa.
    if (spec === bestSpec && rule.effect === 'deny') best = rule;
  }

  if (best == null) return UNDETERMINED;
  return { allowed: best.effect === 'allow', decidedBy: best };
}

/**
 * Rozstrzygnięcie w obrębie JEDNEGO podmiotu — kaskada po obiektach: najwęższa reguła wygrywa,
 * przy remisie głębokości zakaz. To tu działa „prawo na typie bije prawo na kartotece".
 *
 * Eksportowane, bo panel pokazuje rozbicie „rola X: wolno, zespół Y: nie wolno" w okienku
 * „skąd to wynika" — i musi je liczyć tym samym kodem, którym serwer składa wynik końcowy.
 */
export function evaluateOneSubject(
  rules: readonly PermissionRuleInput[],
  subjectKind: PermissionSubjectKind,
  subjectId: string,
  query: PermissionQuery,
): PermissionDecision {
  return evaluateTier(
    rules.filter((r) => r.subjectKind === subjectKind && r.subjectId === subjectId),
    query,
  );
}

/** Warstwa podmiotów — kolejność w `LAYER_ORDER` = priorytet (pierwsza najważniejsza). */
export type PermissionLayer = PermissionSubjectKind;

/** Użytkownik → Zespoły → Role. Rozstrzyga pierwsza warstwa, która się wypowiada. */
export const LAYER_ORDER: readonly PermissionLayer[] = ['user', 'team', 'role'];

/** Podmiot w warstwie (rola / zespół / użytkownik). */
export interface PermissionSubjectRef {
  kind: PermissionSubjectKind;
  id: string;
}

/** Wynik jednego podmiotu — rozbicie dla okienka „skąd to wynika". */
export interface SubjectDecision extends PermissionSubjectRef {
  decision: PermissionDecision;
}

/** Wynik jednej warstwy wraz z wynikami jej podmiotów. */
export interface LayerDecision {
  layer: PermissionLayer;
  decision: PermissionDecision;
  subjects: SubjectDecision[];
}

/** Pełne wyjaśnienie: wynik końcowy, warstwa, która rozstrzygnęła, i rozbicie na warstwy. */
export interface PermissionExplanation extends PermissionDecision {
  /** `null` = żadna warstwa się nie wypowiedziała (odmowa domyślna). */
  decidedLayer: PermissionLayer | null;
  layers: LayerDecision[];
}

/** Podmioty warstwy wyprowadzone z reguł (+ jawnie podane, np. rola bez żadnej reguły). */
function subjectsOfLayer(
  rules: readonly PermissionRuleInput[],
  layer: PermissionLayer,
  extra: readonly PermissionSubjectRef[],
): PermissionSubjectRef[] {
  const seen = new Map<string, PermissionSubjectRef>();
  for (const s of extra) if (s.kind === layer) seen.set(s.id, s);
  for (const r of rules) {
    if (r.subjectKind === layer && !seen.has(r.subjectId)) {
      seen.set(r.subjectId, { kind: layer, id: r.subjectId });
    }
  }
  return [...seen.values()];
}

/**
 * Rozstrzygnięcie JEDNEJ warstwy: każdy podmiot osobno, a między podmiotami zakaz wygrywa.
 *
 * Tropem zakazu jest zakaz NAJWĘŻSZY — komunikat odmowy i okienko „skąd to wynika" wskazują
 * wtedy najbardziej konkretne miejsce, które trzeba zmienić.
 */
function decideLayer(subjects: readonly SubjectDecision[]): PermissionDecision {
  let narrowestDeny: PermissionDecision | null = null;
  let firstAllow: PermissionDecision | null = null;

  for (const { decision } of subjects) {
    if (decision.decidedBy == null) continue;
    if (!decision.allowed) {
      if (
        narrowestDeny?.decidedBy == null ||
        specificity(decision.decidedBy) > specificity(narrowestDeny.decidedBy)
      ) {
        narrowestDeny = decision;
      }
      continue;
    }
    firstAllow ??= decision;
  }

  return narrowestDeny ?? firstAllow ?? UNDETERMINED;
}

/** Wynik jednej warstwy (bez rozbicia na podmioty). */
export function evaluateLayer(
  rules: readonly PermissionRuleInput[],
  layer: PermissionLayer,
  query: PermissionQuery,
): PermissionDecision {
  return decideLayer(
    subjectsOfLayer(rules, layer, []).map((s) => ({
      ...s,
      decision: evaluateOneSubject(rules, s.kind, s.id, query),
    })),
  );
}

/**
 * Pełna drabina z wyjaśnieniem — wynik końcowy + wynik każdej warstwy i każdego jej podmiotu.
 *
 * `subjects` pozwala dopisać podmioty BEZ żadnej reguły (np. druga rola osoby), żeby okienko
 * „skąd to wynika" pokazało je jako „brak ustawień", zamiast je przemilczeć. Na wynik nie wpływa.
 *
 * Wejście to komplet reguł podmiotów użytkownika w JEDNEJ firmie (silnik ładuje go raz na żądanie).
 */
export function explainPermission(
  rules: readonly PermissionRuleInput[],
  query: PermissionQuery,
  subjects: readonly PermissionSubjectRef[] = [],
): PermissionExplanation {
  const layers: LayerDecision[] = LAYER_ORDER.map((layer) => {
    const subjectDecisions = subjectsOfLayer(rules, layer, subjects).map((s) => ({
      ...s,
      decision: evaluateOneSubject(rules, s.kind, s.id, query),
    }));
    return { layer, decision: decideLayer(subjectDecisions), subjects: subjectDecisions };
  });

  const deciding = layers.find((l) => l.decision.decidedBy != null) ?? null;
  return {
    ...(deciding?.decision ?? UNDETERMINED),
    decidedLayer: deciding?.layer ?? null,
    layers,
  };
}

/**
 * Wynik końcowy: pierwsza warstwa (Użytkownik → Zespoły → Role), która się wypowiada.
 *
 * Zasadę w dwóch zdaniach dla administratora:
 *   • **Ważniejsza warstwa rozstrzyga w całości** — jeśli osoba ma własne ustawienie dla tego
 *     obiektu (choćby odziedziczone z szerszego), zespoły i role się nie liczą; jeśli nie ma,
 *     a ma je któryś zespół — rozstrzygają zespoły.
 *   • **W obrębie warstwy jeden zakaz wystarczy** — kilka ról: jeśli którakolwiek zabrania,
 *     nie wolno.
 */
export function evaluatePermission(
  rules: readonly PermissionRuleInput[],
  query: PermissionQuery,
): PermissionDecision {
  for (const layer of LAYER_ORDER) {
    const decision = evaluateLayer(rules, layer, query);
    if (decision.decidedBy != null) return decision;
  }
  return UNDETERMINED;
}
